import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Skeleton from '../components/Skeleton';
import SectionHero from '../sections/SectionHero';
import SectionFeatured from '../sections/SectionFeatured';
import SectionCategories from '../sections/SectionCategories';
import SectionProducts from '../sections/SectionProducts';
import SectionSlider from '../sections/SectionSlider';
import SectionFaq from '../sections/SectionFaq';
import SectionHowToOrder from '../sections/SectionHowToOrder';
import { WidgetText, WidgetImage, WidgetSlider, WidgetFaq, WidgetIcon, WidgetDivider, WidgetMap, WidgetHtml, WidgetVideo, WidgetIconText, WidgetPrice, WidgetPostList, WidgetPostMeta, WidgetPostComments, WidgetTable, WidgetCart, WidgetForm, WidgetProductScroll, WidgetCategoryStrip, WidgetDealRow, WidgetSliderPro, WidgetHeroSlider } from '../sections/WidgetRender';
import { widgetStyleObj, frameStyle } from '../components/widgetStyle';
import { rowVisible, widgetVisible } from '../components/homeRows';
import { setJsonLd, siteOrigin } from '../components/SeoJsonLd';

const legacyLookup = { hero: SectionHero, featured: SectionFeatured, categories: SectionCategories, product_list: SectionProducts, slider: SectionSlider, faq: SectionFaq, how_to_order: SectionHowToOrder };

const SHADOWS = {
  sm: '0 1px 2px rgba(0,0,0,0.06)',
  md: '0 4px 12px rgba(0,0,0,0.08)',
  lg: '0 8px 30px rgba(28,25,23,0.10)',
  xl: '0 16px 48px rgba(28,25,23,0.14)',
};

function renderWidget(w, data, key) {
  const settings = w.settings || {};
  let el;
  switch (w.type) {
    case 'text': el = <WidgetText settings={settings} />; break;
    case 'image': el = <WidgetImage settings={settings} />; break;
    case 'slider':
      el = (settings.slides && settings.slides.length)
        ? <WidgetSlider settings={settings} />
        : <SectionSlider data={data} settings={settings} />;
      break;
    case 'faq': el = <WidgetFaq settings={settings} data={data} />; break;
    case 'icon': el = <WidgetIcon settings={settings} />; break;
    case 'icon_text': el = <WidgetIconText settings={settings} />; break;
    case 'product_scroll': el = <WidgetProductScroll settings={settings} data={data} />; break;
    case 'slider_pro': el = <WidgetSliderPro settings={settings} data={data} />; break;
    case 'hero_slider': el = <WidgetHeroSlider settings={settings} />; break;
    case 'category_strip': el = <WidgetCategoryStrip settings={settings} data={data} />; break;
    case 'deal_row': el = <WidgetDealRow settings={settings} data={data} />; break;
    case 'divider': el = <WidgetDivider settings={settings} />; break;
    case 'map': el = <WidgetMap settings={settings} />; break;
    case 'video': el = <WidgetVideo settings={settings} />; break;
    case 'html': el = <WidgetHtml settings={settings} />; break;
    case 'rates': el = <WidgetPrice settings={settings} />; break;
    case 'post_list': el = <WidgetPostList settings={settings} />; break;
    case 'post_meta': el = <WidgetPostMeta settings={settings} ctx={data} />; break;
    case 'post_comments': el = <WidgetPostComments settings={settings} ctx={data} />; break;
    case 'table': el = <WidgetTable settings={settings} />; break;
    case 'cart_widget': el = <WidgetCart settings={settings} />; break;
    case 'form': el = <WidgetForm settings={settings} />; break;
    case 'container':
      el = (<>{(settings.innerRows || []).filter(rowVisible).map((r, i) => <RowView key={i} row={r} data={data} />)}</>);
      break;
    case 'product_list': el = <SectionProducts data={data} settings={settings} embedded />; break;
    case 'categories': el = <SectionCategories data={data} settings={settings} embedded />; break;
    case 'hero': el = <SectionHero data={data} settings={settings} embedded />; break;
    case 'featured': el = <SectionFeatured data={data} settings={settings} embedded />; break;
    case 'how_to_order': el = <SectionHowToOrder data={data} settings={settings} embedded />; break;
    default: return null;
  }
  if (!el) return null;
  // text paints its own bg/padding/radius; every other widget gets a frame wrapper.
  // Unified style (settings.style) always applies on top (all widget types).
  const fs = w.type === 'text' ? undefined : frameStyle(settings.frame);
  const us = widgetStyleObj(settings.style);
  const wrap = { ...fs, ...us };
  if (!wrap || !Object.keys(wrap).length) return <React.Fragment key={key}>{el}</React.Fragment>;
  return <div key={key} style={wrap}>{el}</div>;
}

export function RowView({ row, data, skeleton }) {
  const rs = row.settings || {};
  const cols = row.columns || [];
  const tl = rs.radiusTL ?? rs.radius ?? 0, tr = rs.radiusTR ?? rs.radius ?? 0;
  const br = rs.radiusBR ?? rs.radius ?? 0, bl = rs.radiusBL ?? rs.radius ?? 0;
  const boxR = (tl || tr || br || bl) ? `${tl}px ${tr}px ${br}px ${bl}px` : undefined;
  const padT = rs.padT ?? rs.padY ?? 32, padB = rs.padB ?? rs.padY ?? 32;
  const padR = rs.padR ?? 0, padL = rs.padL ?? 0;
  // Unified row style (settings.style from the studio «استایل کامل») wins over legacy keys.
  const rowUnified = widgetStyleObj(rs.style);
  // Custom per-column widths (studio «پهنای ستون») → md+ grid template, mobile stacks.
  const colWidths = cols.map(c => Number(c.settings?.width) || 0);
  const hasCustomWidths = colWidths.some(w => w > 0);
  const widthTemplate = hasCustomWidths
    ? cols.map((c, i) => `${Number(c.settings?.width) > 0 ? Number(c.settings.width) : (100 / cols.length)}%`).join(' ')
    : undefined;
  // Full-bleed row (تمام‌عرض واقعی): skips the padded container so sliders/hero touch viewport edges.
  const fullBleed = rs.fullBleed === true;
  return (
    <section style={{
      background: rs.bg && rs.bg !== 'transparent' ? rs.bg : undefined,
      padding: `${padT}px ${padR}px ${padB}px ${padL}px`,
      margin: rs.marginY ? `${rs.marginY}px 0` : undefined,
      borderRadius: boxR, boxShadow: rs.shadow && SHADOWS[rs.shadow],
      overflow: (boxR || rs.shadow) ? 'hidden' : undefined,
      ...rowUnified,
    }}>
      <div className={fullBleed ? undefined : 'hb-container'} style={fullBleed ? { width: '100%' } : undefined}>
        <div className={`hb-cols hb-cols-${Math.min(cols.length, 4)}${rs.keepCols ? ' keep-cols' : ''}${hasCustomWidths ? ' hb-cols-custom' : ''}`}
          style={{ gap: rs.gap ?? 16, ...(widthTemplate ? { '--hb-w': widthTemplate } : {}) }}>
          {cols.map((col, ci) => {
            const cs = col.settings || {};
            const ctl = cs.radiusTL ?? cs.radius ?? 0, ctr = cs.radiusTR ?? cs.radius ?? 0;
            const cbr = cs.radiusBR ?? cs.radius ?? 0, cbl = cs.radiusBL ?? cs.radius ?? 0;
            const cboxR = (ctl || ctr || cbr || cbl) ? `${ctl}px ${ctr}px ${cbr}px ${cbl}px` : undefined;
            // Unified column style wins over legacy keys.
            const colUnified = widgetStyleObj(cs.style);
            return (
              <div key={ci} style={{
                minWidth: 0, display: 'flex', flexDirection: 'column', gap: rs.gap ?? 16,
                background: cs.bg && cs.bg !== 'transparent' ? cs.bg : undefined,
                padding: cs.padding ? `${cs.padding}px` : undefined,
                margin: cs.margin ? `${cs.margin}px` : undefined,
                borderRadius: cboxR, boxShadow: cs.shadow && SHADOWS[cs.shadow],
                overflow: (cboxR || cs.shadow) ? 'hidden' : undefined,
                ...colUnified,
              }}>
                {(col.widgets || []).filter(widgetVisible).map((w, wi) => (
                  skeleton ? <WidgetSkeleton key={`${ci}-${wi}`} type={w.type} /> : renderWidget(w, data, `${ci}-${wi}`)
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/** Per-widget skeleton shaped like the real widget */
function WidgetSkeleton({ type }) {
  switch (type) {
    case 'text':
      return (<div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Skeleton height="16px" /><Skeleton height="16px" width="90%" /><Skeleton height="16px" width="60%" />
      </div>);
    case 'image':
    case 'video':
    case 'slider':
    case 'hero_slider':
    case 'map':
      return <Skeleton height={type === 'slider' ? 300 : 220} style={{ borderRadius: 16 }} />;
    case 'hero':
      return (<div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center', padding: '20px 0' }}>
        <Skeleton height="34px" width="60%" /><Skeleton height="16px" width="80%" /><Skeleton height="44px" width="180px" style={{ borderRadius: 999 }} />
      </div>);
    case 'product_list':
    case 'featured':
      return (<div>
        <Skeleton height="24px" width="40%" style={{ marginBottom: 14 }} />
        <div style={{ display: 'flex', gap: 12 }}>
          {[0, 1, 2].map(i => <Skeleton key={i} height={170} style={{ flex: 1, borderRadius: 14 }} />)}
        </div>
      </div>);
    case 'categories':
      return (<div>
        <Skeleton height="24px" width="40%" style={{ margin: '0 auto 14px' }} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {[0, 1, 2, 3].map(i => <Skeleton key={i} height={110} style={{ borderRadius: 16 }} />)}
        </div>
      </div>);
    case 'faq':
      return (<div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {[0, 1, 2].map(i => <Skeleton key={i} height={52} style={{ borderRadius: 12 }} />)}
      </div>);
    case 'how_to_order':
      return (<div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
        {[0, 1, 2, 3].map(i => <Skeleton key={i} height={110} style={{ borderRadius: 16 }} />)}
      </div>);
    case 'icon':
      return (<div style={{ textAlign: 'center' }}><Skeleton height={48} width={48} style={{ borderRadius: '50%', margin: '0 auto' }} /></div>);
    case 'icon_text':
      return (<div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <Skeleton height={48} width={48} style={{ borderRadius: '50%', flexShrink: 0 }} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}><Skeleton height={18} width="50%" /><Skeleton height={14} /><Skeleton height={14} width="70%" /></div>
      </div>);
    case 'divider':
      return <Skeleton height={8} style={{ margin: '16px 0' }} />;
    case 'html':
      return <Skeleton height={120} style={{ borderRadius: 12 }} />;
    case 'rates':
      return (<div>
        <Skeleton height={24} width="45%" style={{ margin: '0 auto 12px' }} />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 10 }}>
          {[0, 1, 2].map(i => <Skeleton key={i} height={38} width={110} style={{ borderRadius: 12 }} />)}
        </div>
        <Skeleton height={150} style={{ borderRadius: 16 }} />
      </div>);
    case 'table':
      return (<div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Skeleton height={36} style={{ borderRadius: 10 }} />
        {[0, 1, 2].map(i => <Skeleton key={i} height={30} style={{ borderRadius: 8 }} />)}
      </div>);
    case 'post_list':
      return (<div>
        <Skeleton height={24} width="40%" style={{ marginBottom: 14 }} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
          {[0, 1, 2].map(i => (
            <div key={i}><Skeleton height={150} style={{ borderRadius: 14 }} /><Skeleton height={16} width="80%" style={{ marginTop: 8 }} /><Skeleton height={13} width="50%" style={{ marginTop: 6 }} /></div>
          ))}
        </div>
      </div>);
    case 'post_meta':
      return (<div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center' }}>
        <Skeleton height={30} width="60%" /><Skeleton height={14} width={140} /><Skeleton height={260} style={{ borderRadius: 16 }} />
      </div>);
    case 'post_comments':
      return (<div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <Skeleton height={24} width="35%" />
        {[0, 1].map(i => <Skeleton key={i} height={64} style={{ borderRadius: 12 }} />)}
      </div>);
    case 'cart_widget':
      return (<div style={{ border: '1px solid #E4E4E7', borderRadius: 16, padding: 18, maxWidth: 560, margin: '0 auto' }}>
        <Skeleton height={22} width="40%" style={{ margin: '0 auto 12px' }} />
        <Skeleton height={16} /><Skeleton height={16} width="80%" style={{ marginTop: 8 }} /><Skeleton height={16} width="60%" style={{ marginTop: 8 }} />
        <Skeleton height={44} style={{ borderRadius: 12, marginTop: 12 }} />
      </div>);
    case 'form':
      return (<div style={{ border: '1px solid #E4E4E7', borderRadius: 16, padding: 18, maxWidth: 560, margin: '0 auto' }}>
        <Skeleton height={24} width="45%" style={{ margin: '0 auto 14px' }} />
        <Skeleton height={44} style={{ borderRadius: 12 }} />
        <Skeleton height={44} style={{ borderRadius: 12, marginTop: 10 }} />
        <Skeleton height={80} style={{ borderRadius: 12, marginTop: 10 }} />
        <Skeleton height={46} style={{ borderRadius: 12, marginTop: 12 }} />
      </div>);
    case 'container':
      return (<div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <Skeleton height={40} style={{ borderRadius: 12 }} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Skeleton height={110} style={{ borderRadius: 12 }} /><Skeleton height={110} style={{ borderRadius: 12 }} />
        </div>
      </div>);
    default:
      return <Skeleton height={120} style={{ borderRadius: 12 }} />;
  }
}

// Sections can be a legacy array OR per-platform { desktop, tablet, mobile }
export function resolveRows(sections, vw) {
  if (!sections) return [];
  if (Array.isArray(sections)) return sections;
  if (typeof sections === 'object') {
    const key = vw < 768 ? 'mobile' : vw < 1024 ? 'tablet' : 'desktop';
    return (sections[key] && sections[key].length ? sections[key] : null)
      || (sections.desktop && sections.desktop.length ? sections.desktop : null)
      || (sections.mobile && sections.mobile.length ? sections.mobile : null)
      || (sections.tablet && sections.tablet.length ? sections.tablet : null)
      || [];
  }
  return [];
}

/** Apply page SEO to document head (supports flat seo_* keys and legacy page keys) */
export function applySeo(seo = {}, fallbackTitle) {
  seo = seo || {};
  const title = seo.seo_title || seo.title || fallbackTitle || 'فروشگاه';
  document.title = title;
  const setMeta = (key, attr, content) => {
    if (!content) return;
    let el = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute(attr, key);
      document.head.appendChild(el);
    }
    el.setAttribute('content', content);
  };
  const desc = seo.seo_desc || seo.description || '';
  setMeta('description', 'name', desc);
  setMeta('keywords', 'name', seo.seo_keywords || seo.keywords || '');
  setMeta('robots', 'name', seo.robots || '');
  setMeta('og:title', 'property', seo.og_title || title);
  setMeta('og:description', 'property', seo.og_desc || desc);
  if (seo.og_image) setMeta('og:image', 'property', seo.og_image);
  setMeta('og:type', 'property', 'website');
  const canon = seo.canonical || '';
  let link = document.head.querySelector('link[rel="canonical"]');
  if (canon) {
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', canon);
  } else if (link) {
    link.remove();
  }
  // PWA: مانیفست داینامیک با نام فروشگاه (نصب با اسم درست)
  try {
    const man = {
      name: title, short_name: title.slice(0, 18) || 'فروشگاه',
      start_url: '.', display: 'standalone', direction: 'rtl', lang: 'fa',
      background_color: '#ffffff', theme_color: '#1d4ed8',
      icons: [{ src: '/logo192.png', type: 'image/png', sizes: '192x192' }, { src: '/logo512.png', type: 'image/png', sizes: '512x512' }],
    };
    let ml = document.head.querySelector('link[data-dyn-manifest]');
    if (!ml) {
      ml = document.createElement('link');
      ml.setAttribute('rel', 'manifest');
      ml.setAttribute('data-dyn-manifest', '1');
      document.head.appendChild(ml);
    }
    ml.setAttribute('href', URL.createObjectURL(new Blob([JSON.stringify(man)], { type: 'application/json' })));
  } catch {}
}

export default function HomePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [vw, setVw] = useState(() => typeof window !== 'undefined' ? window.innerWidth : 1280);
  const [cached] = useState(() => {
    try { return JSON.parse(localStorage.getItem('home_sections_cache') || 'null'); }
    catch { return null; }
  });
  const [customPage, setCustomPage] = useState(null);

  useEffect(() => {
    const f = () => setVw(window.innerWidth);
    window.addEventListener('resize', f);
    return () => window.removeEventListener('resize', f);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get('/home');
        setData(r.data);
        try { localStorage.setItem('home_sections_cache', JSON.stringify(r.data.sections || [])); } catch {}
        try {
          const s = await axios.get('/site-settings');
          const slug = s.data?.homepage;
          if (slug && slug !== 'home') {
            const p = await axios.get(`/pages/${slug}`);
            setCustomPage(p.data);
            applySeo(p.data.seo || {}, p.data.title);
          }
        } catch {}
        setLoading(false);
      }
      catch { setError('خطا در بارگذاری صفحه'); setLoading(false); }
    })();
  }, []);

  if (loading) {
    const cacheRows = resolveRows(cached, vw).filter(rowVisible);
    if (cacheRows.length) {
      return (
        <div>
          {cacheRows.map((s, i) => (
            s.columns ? <RowView key={s.id || i} row={s} skeleton /> : null
          ))}
        </div>
      );
    }
    return (
      <div className="container" style={{ paddingTop: 'var(--space-4)' }}>
        <Skeleton height="300px" />
        <div className="grid-products" style={{ marginTop: '30px' }}>
          {[1, 2, 3].map(i => (
            <div key={i} className="card"><div className="card-media"><Skeleton height="100%" /></div><div style={{ padding: 16 }}><Skeleton height="20px" width="70%" /><Skeleton height="14px" style={{ marginTop: 10 }} /></div></div>
          ))}
        </div>
      </div>
    );
  }
  if (error || !data) return <div style={{ padding: '100px 20px', textAlign: 'center', color: 'red' }}>{error || 'خطا'}</div>;

  const srcSections = (customPage && customPage.platforms) ? customPage.platforms : data.sections;
  const enabled = resolveRows(srcSections, vw).filter(rowVisible);

  // GEO/AEO: Organization + WebSite + FAQPage
  const ldHome = (() => {
    try {
      const origin = siteOrigin();
      const siteName = (typeof document !== 'undefined' && document.title) ? document.title : '';
      if (!siteName) return null;
      const faqs = Array.isArray(data.faqs) ? data.faqs.slice(0, 20) : [];
      return {
        '@context': 'https://schema.org',
        '@graph': [
          { '@type': 'Organization', '@id': `${origin}/#org`, name: siteName, url: `${origin}/` },
          { '@type': 'WebSite', '@id': `${origin}/#site`, url: `${origin}/`, name: siteName, publisher: { '@id': `${origin}/#org` } },
          ...(faqs.length ? [{
            '@type': 'FAQPage',
            mainEntity: faqs.filter(f => f.question && f.answer).map(f => ({
              '@type': 'Question', name: f.question,
              acceptedAnswer: { '@type': 'Answer', text: String(f.answer).replace(/<[^>]*>/g, ' ').slice(0, 2000) },
            })),
          }] : []),
        ],
      };
    } catch { return null; }
  })();
  setJsonLd('home-org', ldHome);

  return (
    <div>
      {enabled.map((s, i) => {
        // New row/column structure
        if (s.columns) return <RowView key={s.id || i} row={s} data={data} />;
        // Legacy flat sections
        const C = legacyLookup[s.type];
        if (!C) return null;
        return <C key={i} data={data} settings={s.settings || {}} />;
      })}
    </div>
  );
}
